/**
 * IndexAnalysisPage — SEO landing page for Nifty 50 and Bank Nifty AI analysis.
 * Routes: /nifty-ai-analysis, /bank-nifty-ai-analysis
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  TrendingUp, TrendingDown, RefreshCw, ArrowRight,
  BarChart3, Shield, Brain, ChevronDown, ChevronUp,
} from 'lucide-react';
import SeoMeta from './SeoMeta';
import { useIndices } from '../hooks/useIndices';
import { fetchHistorical, type OHLCCandle } from '../services/marketDataApi';
import { fmt, fmtPrice, rangePosition, educationalContext } from '../lib/marketContext';

// ─── Types ────────────────────────────────────────────────────────────────────

interface IndexConfig {
  symbol: string;
  name: string;
  shortName: string;
  slug: string;
  description: string;
  seoTitle: string;
  seoDescription: string;
  seoKeywords: string;
  faqItems: { q: string; a: string }[];
}

export const NIFTY_CONFIG: IndexConfig = {
  symbol: '^NSEI',
  name: 'Nifty 50',
  shortName: 'NIFTY',
  slug: 'nifty-ai-analysis',
  description: 'India\'s benchmark index tracking the top 50 companies by free-float market capitalisation on NSE.',
  seoTitle: 'Nifty 50 AI Analysis Today — Live NSE Index Research | IndianStockMarketGPT',
  seoDescription: 'Free AI-powered educational analysis of the Nifty 50 index. Live price, day range, VIX context, and plain-English breakdown of today\'s market data. Not investment advice.',
  seoKeywords: 'nifty 50 analysis today, nifty AI analysis, nifty 50 educational analysis, nifty 50 technical analysis India, nifty 50 live data, free nifty analysis India, nifty index research',
  faqItems: [
    { q: 'What is Nifty 50?', a: 'The Nifty 50 is India\'s benchmark stock market index, tracking the 50 largest companies by free-float market capitalisation listed on the National Stock Exchange (NSE). It covers 13 sectors and represents approximately 65% of the total NSE market capitalisation.' },
    { q: 'How does IndianStockMarketGPT analyse the Nifty 50?', a: 'IndianStockMarketGPT uses AI to synthesise live price data — day range, gap vs previous close, VIX context — and explains what the data means in historical and educational terms. It does not predict future price movements. All analysis is for educational purposes only.' },
    { q: 'Is this Nifty analysis investment advice?', a: 'No. All content on this page is for educational and informational purposes only. IndianStockMarketGPT is not a SEBI-registered investment adviser. Always consult a qualified SEBI-registered adviser before making any investment decision.' },
    { q: 'What is India VIX and why does it matter for Nifty?', a: 'India VIX (Volatility Index) measures the market\'s expectation of near-term price fluctuations in Nifty 50. A low VIX (below 15) historically suggests calmer markets; a high VIX (above 20) indicates elevated uncertainty. It is a measure of market sentiment, not a trading signal.' },
    { q: 'How often is this data updated?', a: 'Live price data refreshes every 60 seconds during NSE market hours (9:15 AM – 3:30 PM IST, Monday to Friday). Data is sourced from Yahoo Finance and is approximately 15 minutes delayed on the free tier.' },
  ],
};

export const BANK_NIFTY_CONFIG: IndexConfig = {
  symbol: '^NSEBANK',
  name: 'Bank Nifty',
  shortName: 'BANKNIFTY',
  slug: 'bank-nifty-ai-analysis',
  description: 'NSE\'s banking sector index tracking 12 of India\'s most liquid and large-cap banking stocks.',
  seoTitle: 'Bank Nifty AI Analysis Today — Live NSE Banking Index Research | IndianStockMarketGPT',
  seoDescription: 'Free AI-powered educational analysis of Bank Nifty. Live price, day range, and plain-English breakdown of today\'s banking sector market data. Not investment advice.',
  seoKeywords: 'bank nifty analysis today, bank nifty AI analysis, bank nifty educational analysis, bank nifty technical analysis India, bank nifty live data, free bank nifty analysis, banknifty research India',
  faqItems: [
    { q: 'What is Bank Nifty?', a: 'Bank Nifty (officially Nifty Bank) is an index comprising 12 of the most liquid and large-cap Indian banking stocks listed on NSE. It includes public and private sector banks and is one of the most actively tracked indices by Indian traders.' },
    { q: 'How does Bank Nifty relate to Nifty 50?', a: 'Bank Nifty tracks banking stocks specifically, while Nifty 50 covers 13 sectors broadly. Banking has historically been one of the largest sectoral weights in Nifty 50, so Bank Nifty movements often influence the broader index. However, they can diverge significantly based on sector-specific news (RBI policy, NPA cycles, credit growth data).' },
    { q: 'Is this Bank Nifty analysis investment advice?', a: 'No. All content on this page is for educational and informational purposes only. IndianStockMarketGPT is not a SEBI-registered investment adviser. Always consult a qualified SEBI-registered adviser before making any investment decision.' },
    { q: 'What factors typically influence Bank Nifty?', a: 'Bank Nifty is historically sensitive to: RBI monetary policy decisions (repo rate changes), quarterly earnings from major banks, NPA (Non-Performing Assets) disclosures, credit growth data from RBI, and global banking sector sentiment. This is educational context — not a predictive framework.' },
    { q: 'How often is this data updated?', a: 'Live price data refreshes every 60 seconds during NSE market hours (9:15 AM – 3:30 PM IST, Monday to Friday). Data is sourced from Yahoo Finance and is approximately 15 minutes delayed on the free tier.' },
  ],
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function buildSparkPoints(candles: OHLCCandle[], w: number, h: number): string {
  if (candles.length < 2) return '';
  const prices = candles.map(c => c.close);
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const range = max - min || 1;
  return prices
    .map((p, i) => `${(i / (prices.length - 1)) * w},${h - ((p - min) / range) * (h - 6) - 3}`)
    .join(' ');
}


// ─── FAQ Item ─────────────────────────────────────────────────────────────────

const FAQ: React.FC<{ q: string; a: string }> = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-100 last:border-b-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left group"
      >
        <span className="text-sm font-medium text-slate-800 group-hover:text-navy-500 transition-colors pr-4">{q}</span>
        {open ? <ChevronUp className="w-4 h-4 text-navy-500 shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />}
      </button>
      {open && <p className="text-sm text-slate-600 leading-relaxed pb-5">{a}</p>}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

interface Props { config: IndexConfig }

const IndexAnalysisPage: React.FC<Props> = ({ config }) => {
  const { indices, loading, lastUpdated, refresh } = useIndices();
  const [candles, setCandles] = useState<OHLCCandle[]>([]);

  const idx    = indices.find(i => i.symbol === config.symbol);
  const vix    = indices.find(i => i.symbol === '^INDIAVIX');

  useEffect(() => {
    fetchHistorical(config.symbol, '1d', '5m').then(setCandles).catch(() => {});
  }, [config.symbol]);

  const up = (idx?.changePct ?? 0) >= 0;
  const sparkPoints = buildSparkPoints(candles, 280, 56);
  const pos = idx ? rangePosition(idx.price, idx.low, idx.high) : null;

  const vixNum = vix?.price ?? 0;
  const vixLabel =
    vixNum < 12 ? { text: 'Very calm', color: 'text-green-600' } :
    vixNum < 16 ? { text: 'Calm',      color: 'text-green-600' } :
    vixNum < 20 ? { text: 'Cautious',  color: 'text-amber-600' } :
    vixNum < 25 ? { text: 'Nervous',   color: 'text-orange-600' } :
                  { text: 'Fear',      color: 'text-red-600'    };

  const eduLines = idx
    ? educationalContext(idx.changePct, idx.price, idx.open, idx.high, idx.low, idx.prevClose, config.name)
    : [];

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: config.faqItems.map(f => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <>
      <SeoMeta
        title={config.seoTitle}
        description={config.seoDescription}
        keywords={config.seoKeywords}
        url={`https://indianstockmarketgpt.com/${config.slug}`}
        type="website"
        siteName="IndianStockMarketGPT"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      <div className="min-h-screen bg-white text-slate-900">

        {/* ── Hero ─────────────────────────────────────────────────────── */}
        <section className="pt-12 pb-10 px-4 sm:px-6 lg:px-8 border-b border-slate-100 bg-slate-50">
          <div className="max-w-4xl mx-auto">
            <p className="text-xs text-slate-500 tracking-[0.25em] uppercase mb-5 text-center">
              AI Research · NSE · Educational Only · Not Investment Advice
            </p>

            <h1 className="text-3xl sm:text-4xl font-bold font-serif text-slate-900 text-center mb-2 leading-tight">
              {config.name} AI Analysis
            </h1>
            <p className="text-sm text-navy-500 tracking-[0.15em] uppercase font-semibold text-center mb-6">
              AI investigates. You decide.
            </p>
            <p className="text-sm text-slate-600 text-center mb-10 max-w-xl mx-auto leading-relaxed">
              {config.description} Live data synthesised into plain-English educational context — no predictions, no advice.
            </p>

            {/* ── Live Price Card ── */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 max-w-2xl mx-auto shadow-sm">
              {loading && !idx ? (
                <div className="h-24 flex items-center justify-center">
                  <div className="w-6 h-6 border-2 border-navy-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : idx ? (
                <>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <p className="text-xs text-slate-500 tracking-widest uppercase mb-1">{config.shortName} · NSE</p>
                      <p className="text-4xl font-bold font-mono text-slate-900 leading-none">
                        {fmt(idx.price)}
                      </p>
                      <p className={`text-base font-semibold mt-1 ${up ? 'text-green-600' : 'text-red-600'}`}>
                        {up ? '+' : ''}{fmt(idx.change)} ({up ? '+' : ''}{idx.changePct.toFixed(2)}%)
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1.5">
                      {up
                        ? <TrendingUp className="w-8 h-8 text-green-600" />
                        : <TrendingDown className="w-8 h-8 text-red-600" />}
                      <button
                        onClick={refresh}
                        className="text-slate-400 hover:text-navy-500 transition-colors"
                        title="Refresh data"
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                      </button>
                      {lastUpdated && (
                        <p className="text-[10px] text-slate-400">
                          {lastUpdated.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })} IST
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Sparkline */}
                  {sparkPoints && (
                    <svg viewBox="0 0 280 56" className="w-full h-14 mb-4" preserveAspectRatio="none">
                      <polyline
                        points={sparkPoints}
                        fill="none"
                        stroke={up ? '#16a34a' : '#dc2626'}
                        strokeWidth="1.5"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}

                  {/* Day stats grid */}
                  <div className="grid grid-cols-4 gap-3 mb-4">
                    {[
                      { label: 'Open',       value: fmtPrice(idx.open)      },
                      { label: 'High',       value: fmtPrice(idx.high)      },
                      { label: 'Low',        value: fmtPrice(idx.low)       },
                      { label: 'Prev Close', value: fmtPrice(idx.prevClose) },
                    ].map(({ label, value }) => (
                      <div key={label} className="bg-slate-50 border border-slate-100 rounded-lg px-3 py-2 text-center">
                        <p className="text-[10px] text-slate-500 mb-0.5">{label}</p>
                        <p className="text-xs font-mono font-semibold text-slate-900">{value}</p>
                      </div>
                    ))}
                  </div>

                  {/* Day range bar — hidden when the feed has no valid range yet */}
                  {pos !== null && (
                  <div className="mb-2">
                    <div className="flex justify-between text-[10px] text-slate-500 mb-1">
                      <span>Day Low: {fmt(idx.low)}</span>
                      <span>Day High: {fmt(idx.high)}</span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${up ? 'bg-green-500' : 'bg-red-500'}`}
                        style={{ width: `${pos}%` }}
                      />
                    </div>
                    <p className="text-[10px] text-slate-500 mt-1 text-center">
                      Current price at {pos}th percentile of today's range
                    </p>
                  </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-slate-500 text-center py-6">Live data unavailable — market may be closed or data delayed.</p>
              )}
            </div>

            <p className="text-[10px] text-slate-400 text-center mt-3">
              Data ~15 min delayed · Educational only · Not investment advice · Not SEBI registered
            </p>
          </div>
        </section>

        {/* ── VIX + Context ─────────────────────────────────────────────── */}
        {vixNum > 0 && (
          <section className="py-8 px-4 sm:px-6 lg:px-8 border-b border-slate-100 bg-white">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-base font-semibold text-slate-800 mb-4 text-center">
                Market Volatility Context (India VIX)
              </h2>
              <div className="flex items-center justify-center gap-6 flex-wrap">
                <div className="text-center">
                  <p className="text-3xl font-bold font-mono text-slate-900">{vixNum.toFixed(2)}</p>
                  <p className={`text-sm font-semibold ${vixLabel.color}`}>{vixLabel.text}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">India VIX</p>
                </div>
                <div className="max-w-sm">
                  <p className="text-xs text-slate-600 leading-relaxed">
                    India VIX measures near-term expected volatility in Nifty 50. Historically, readings below 15 have been associated with calmer market conditions; readings above 20 reflect elevated uncertainty. This is educational context, not a trading signal.
                  </p>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* ── AI Educational Analysis ───────────────────────────────────── */}
        <section className="py-10 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-2 mb-6">
              <Brain className="w-4 h-4 text-navy-500" />
              <h2 className="text-base font-semibold text-slate-800">
                What Today's Data Shows — Educational Context
              </h2>
              <span className="text-[10px] text-slate-500 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-full ml-auto">
                Educational only
              </span>
            </div>

            {eduLines.length > 0 ? (
              <div className="space-y-4">
                {eduLines.map((line, i) => (
                  <div key={i} className="flex gap-3 p-4 bg-slate-50 rounded-xl border border-slate-100">
                    <div className="w-5 h-5 rounded-full bg-navy-100 flex items-center justify-center shrink-0 mt-0.5">
                      <span className="text-[10px] font-bold text-navy-600">{i + 1}</span>
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed">{line}</p>
                  </div>
                ))}
                <p className="text-xs text-slate-400 mt-2 leading-relaxed">
                  All analysis above is AI-generated historical/educational context based on today's live price data. It does not constitute a prediction of future price movement or investment advice.
                </p>
              </div>
            ) : (
              <div className="p-6 bg-slate-50 rounded-xl border border-slate-100 text-center">
                <p className="text-sm text-slate-500">Loading educational analysis — waiting for live data…</p>
              </div>
            )}

            {/* Deeper research CTA */}
            <div className="mt-8 p-5 bg-navy-50 rounded-xl border border-navy-100 flex flex-col sm:flex-row items-center gap-4">
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800 mb-1">Research individual stocks with AI</p>
                <p className="text-xs text-slate-600 leading-relaxed">
                  Ask anything about any NSE or BSE stock — technicals, fundamentals, sector context. Free. No login required.
                </p>
              </div>
              <Link
                to="/ask"
                className="inline-flex items-center gap-2 bg-navy-500 hover:bg-navy-600 text-white font-semibold px-5 py-2.5 rounded-lg transition-all text-sm shrink-0"
              >
                Ask the AI
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* ── Related Indices ───────────────────────────────────────────── */}
        {indices.length > 0 && (
          <section className="py-8 px-4 sm:px-6 lg:px-8 border-t border-slate-100 bg-slate-50">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-widest mb-5 text-center">
                Related Indices — Live
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                {indices
                  .filter(i => i.symbol !== config.symbol && i.symbol !== '^INDIAVIX')
                  .map(i => {
                    const u = i.changePct >= 0;
                    return (
                      <div key={i.symbol} className="bg-white border border-slate-200 rounded-xl p-3 text-center shadow-sm">
                        <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-1 truncate">{i.name}</p>
                        <p className="text-sm font-bold font-mono text-slate-900">{fmt(i.price)}</p>
                        <p className={`text-xs font-medium ${u ? 'text-green-600' : 'text-red-600'}`}>
                          {u ? '+' : ''}{i.changePct.toFixed(2)}%
                        </p>
                      </div>
                    );
                  })}
              </div>
              <p className="text-[10px] text-slate-400 text-center mt-3">~15 min delayed · Educational reference only</p>
            </div>
          </section>
        )}

        {/* ── What is this index ────────────────────────────────────────── */}
        <section className="py-10 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex gap-3">
                <BarChart3 className="w-5 h-5 text-navy-500 shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-sm font-semibold text-slate-800 mb-1">What is {config.name}?</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{config.description}</p>
                </div>
              </div>
              <div className="flex gap-3">
                <Brain className="w-5 h-5 text-navy-500 shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-sm font-semibold text-slate-800 mb-1">How AI analyses it</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">
                    AI synthesises live price data — open, high, low, gap vs previous close — and explains what it means in historical context. No predictions.
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <Shield className="w-5 h-5 text-navy-500 shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-sm font-semibold text-slate-800 mb-1">Educational only</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">
                    All analysis is historical and informational. IndianStockMarketGPT is not SEBI registered. This is not investment advice.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── FAQ ──────────────────────────────────────────────────────── */}
        <section className="py-10 px-4 sm:px-6 lg:px-8 border-t border-slate-100 bg-slate-50">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-xl font-bold font-serif text-slate-900 mb-8 text-center">
              Common questions about {config.name} analysis
            </h2>
            <div className="bg-white rounded-2xl border border-slate-200 px-6 shadow-sm">
              {config.faqItems.map(f => <FAQ key={f.q} q={f.q} a={f.a} />)}
            </div>
          </div>
        </section>

        {/* ── Final CTA ─────────────────────────────────────────────────── */}
        <section className="py-10 px-4 sm:px-6 lg:px-8 border-t border-slate-100">
          <div className="max-w-xl mx-auto text-center">
            <p className="text-xs text-slate-500 tracking-[0.2em] uppercase mb-4">AI investigates. You decide.</p>
            <h2 className="text-xl font-bold font-serif text-slate-900 mb-3">
              Research any stock or index for free
            </h2>
            <p className="text-sm text-slate-600 mb-6 leading-relaxed">
              Ask anything about NSE or BSE markets. AI synthesises historical data into plain English. No sign-up. No investment advice.
            </p>
            <Link
              to="/ask"
              className="inline-flex items-center gap-2 bg-navy-500 hover:bg-navy-600 text-white font-semibold px-7 py-3 rounded-lg transition-all text-sm"
            >
              Start researching free
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        {/* ── Disclaimer ────────────────────────────────────────────────── */}
        <section className="py-6 px-4 sm:px-6 lg:px-8 border-t border-slate-100 bg-slate-50">
          <div className="max-w-4xl mx-auto">
            <p className="text-xs text-slate-400 text-center leading-relaxed">
              <strong className="text-slate-500">Educational content only.</strong>{' '}
              All data and analysis on this page is for informational and educational purposes only. It does not constitute investment advice, a recommendation to buy or sell any security, or a solicitation. IndianStockMarketGPT is not a SEBI-registered research analyst or investment adviser. Live data is sourced from Yahoo Finance and is approximately 15 minutes delayed. Past performance of any index is not indicative of future results. Always consult a SEBI-registered investment adviser before making financial decisions.
            </p>
          </div>
        </section>

      </div>
    </>
  );
};

export default IndexAnalysisPage;
